import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'

/* ---------- Buttons: blue pill (primary), grey pill, black pill (launch), text ---------- */

export const Button = ({
   children,
   onClick,
   variant = 'primary',
   size = 'md',
   disabled,
   className,
   type = 'button',
   testId,
   full,
}: {
   children: ReactNode
   onClick?: () => void
   variant?: 'primary' | 'secondary' | 'ghost' | 'black' | 'outline'
   size?: 'sm' | 'md' | 'lg'
   disabled?: boolean
   className?: string
   type?: 'button' | 'submit'
   testId?: string
   full?: boolean
}) => (
   <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
         'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all select-none whitespace-nowrap',
         size === 'sm' && 'h-9 px-4 text-[13px]',
         size === 'md' && 'h-12 px-6 text-[15px]',
         size === 'lg' && 'h-14 px-7 text-[16px]',
         variant === 'primary' && 'bg-[var(--fr-blue)] text-white hover:bg-[var(--fr-blue-dark)] shadow-[0_8px_20px_-10px_rgba(47,107,255,0.6)]',
         variant === 'black' && 'bg-[var(--fr-black)] text-white hover:bg-[#232330]',
         variant === 'secondary' && 'bg-[var(--fr-surface-2)] text-[var(--fr-text)] hover:bg-[#ebebef]',
         variant === 'outline' && 'bg-white text-[var(--fr-text)] border border-[var(--fr-border)] hover:border-[var(--fr-text-3)]',
         variant === 'ghost' && 'bg-transparent text-[var(--fr-text-2)] hover:text-[var(--fr-text)] hover:bg-[var(--fr-surface-2)]',
         disabled && '!bg-[var(--fr-surface-2)] !text-[var(--fr-text-3)] !shadow-none cursor-not-allowed pointer-events-none',
         full && 'w-full',
         className
      )}
   >
      {children}
   </button>
)

export const LinkButton = ({ children, onClick, className, testId }: { children: ReactNode; onClick: () => void; className?: string; testId?: string }) => (
   <button type="button" onClick={onClick} data-testid={testId} className={cn('text-[var(--fr-blue)] text-[14px] font-semibold hover:underline', className)}>
      {children}
   </button>
)

/* ---------- Inputs ---------- */

export const Input = ({
   value,
   onChange,
   placeholder,
   label,
   hint,
   error,
   success,
   type = 'text',
   autoFocus,
   onEnter,
   testId,
   leading,
   trailing,
   size = 'md',
   className,
   align,
}: {
   value: string
   onChange: (v: string) => void
   placeholder?: string
   label?: ReactNode
   hint?: ReactNode
   error?: string | null
   /** A green state with a message, e.g. a verified key. */
   success?: ReactNode
   type?: string
   autoFocus?: boolean
   onEnter?: () => void
   testId?: string
   leading?: ReactNode
   trailing?: ReactNode
   size?: 'md' | 'lg'
   className?: string
   align?: 'center'
}) => (
   <label className={cn('block', className)}>
      {label && <span className="block text-[13px] font-medium text-[var(--fr-text-2)] mb-1.5">{label}</span>}
      <span className="relative block">
         {leading && <span className={cn('absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none', success ? 'text-[var(--fr-good)]' : 'text-[var(--fr-text-3)]')}>{leading}</span>}
         <input
            type={type}
            value={value}
            autoFocus={autoFocus}
            placeholder={placeholder}
            onChange={(e) => onChange(e.currentTarget.value)}
            onKeyDown={(e) => {
               if (e.key === 'Enter' && onEnter) onEnter()
            }}
            data-testid={testId}
            className={cn(
               'w-full rounded-2xl border bg-white text-[var(--fr-text)] transition-colors focus:outline-none',
               success ? 'border-[var(--fr-good)] bg-[var(--fr-good-tint)]/40' : error ? 'border-red-400' : 'border-[var(--fr-border)] hover:border-[var(--fr-border-strong)] focus:border-[var(--fr-blue)] focus:ring-4 focus:ring-[var(--fr-blue-tint)]',
               size === 'md' ? 'h-12 text-[15px]' : 'h-14 text-[17px]',
               leading ? 'pl-11' : 'pl-4',
               trailing || success ? 'pr-28' : 'pr-4',
               align === 'center' && 'text-center'
            )}
         />
         {(trailing || success) && <span className={cn('absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-semibold', success ? 'text-[var(--fr-good)]' : 'text-[var(--fr-text-3)]')}>{success || trailing}</span>}
      </span>
      {(hint || error) && <span className={cn('block text-[12px] mt-1.5', error ? 'text-red-500' : 'text-[var(--fr-text-3)]')}>{error || hint}</span>}
   </label>
)

export const Select = ({ value, onChange, options, label, placeholder = 'Choose', testId, className }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] | string[]; label?: ReactNode; placeholder?: string; testId?: string; className?: string }) => (
   <label className={cn('block', className)}>
      {label && <span className="block text-[13px] font-medium text-[var(--fr-text-2)] mb-1.5">{label}</span>}
      <span className="relative block">
         <select value={value} onChange={(e) => onChange(e.currentTarget.value)} data-testid={testId} className="w-full h-12 appearance-none rounded-2xl border border-[var(--fr-border)] bg-white pl-4 pr-10 text-[15px] hover:border-[var(--fr-border-strong)] focus:border-[var(--fr-blue)] focus:outline-none focus:ring-4 focus:ring-[var(--fr-blue-tint)]">
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((o) => {
               const v = typeof o === 'string' ? o : o.value
               const l = typeof o === 'string' ? o : o.label
               return (
                  <option key={v} value={v}>
                     {l}
                  </option>
               )
            })}
         </select>
         <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--fr-text-3)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 9l6 6 6-6" /></svg>
         </span>
      </span>
   </label>
)

/* ---------- Chips: colour by kind ---------- */

export const Chip = ({ children, onRemove, onAdd, kind, testId, size = 'md' }: { children: ReactNode; onRemove?: () => void; onAdd?: () => void; kind?: 'stage' | 'tag' | 'source' | 'pond' | string; testId?: string; size?: 'md' | 'lg' }) => {
   const tone =
      kind === 'stage'
         ? 'bg-[var(--fr-stage-tint)] text-[var(--fr-stage)]'
         : kind === 'tag'
           ? 'bg-[var(--fr-tag-tint)] text-[var(--fr-tag)]'
           : kind === 'source'
             ? 'bg-[var(--fr-source-tint)] text-[var(--fr-source)]'
             : kind === 'pond'
               ? 'bg-[var(--fr-pond-tint)] text-[var(--fr-pond)]'
               : 'bg-[var(--fr-surface-2)] text-[var(--fr-text)]'
   const base = cn('inline-flex items-center gap-1.5 rounded-full font-medium leading-none transition-colors', size === 'md' ? 'px-3 h-9 text-[13px]' : 'px-4 h-11 text-[15px]')
   if (onAdd)
      return (
         <button type="button" onClick={onAdd} data-testid={testId} className={cn(base, 'border border-dashed border-[var(--fr-border-strong)] bg-white text-[var(--fr-text-2)] hover:border-[var(--fr-blue)] hover:text-[var(--fr-blue)]')}>
            <span className="font-semibold">+</span>
            {children}
            {kind && <span className="text-[10px] uppercase tracking-wide opacity-60">{kind}</span>}
         </button>
      )
   return (
      <span data-testid={testId} className={cn(base, tone)}>
         {children}
         {kind && (kind === 'stage' || kind === 'tag') && <span className="text-[10px] uppercase tracking-wide opacity-60">{kind}</span>}
         {onRemove && (
            <button type="button" aria-label={`Remove ${typeof children === 'string' ? children : ''}`} onClick={onRemove} className="ml-0.5 -mr-1 w-5 h-5 rounded-full opacity-60 hover:opacity-100 hover:bg-black/10 flex items-center justify-center text-[14px] leading-none">
               ×
            </button>
         )}
      </span>
   )
}

export const Tile = ({ selected, onClick, children, className, testId, role = 'radio', disabled }: { selected: boolean; onClick: () => void; children: ReactNode; className?: string; testId?: string; role?: 'radio' | 'checkbox' | 'button'; disabled?: boolean }) => (
   <button
      type="button"
      role={role}
      aria-checked={role === 'button' ? undefined : selected}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
         'text-left rounded-2xl border bg-white p-4 transition-all w-full',
         selected ? 'border-[var(--fr-blue)] ring-4 ring-[var(--fr-blue-tint)]' : 'border-[var(--fr-border)] hover:border-[var(--fr-border-strong)]',
         disabled && 'opacity-40 cursor-not-allowed',
         className
      )}
   >
      {children}
   </button>
)

export const Radio = ({ on }: { on: boolean }) => (
   <span className={cn('flex shrink-0 items-center justify-center w-5 h-5 rounded-full border-2 transition-colors', on ? 'border-[var(--fr-blue)]' : 'border-[var(--fr-border-strong)]')}>
      {on && <span className="w-2.5 h-2.5 rounded-full bg-[var(--fr-blue)]" />}
   </span>
)

const HUES = [214, 160, 28, 290, 340, 190]
export const Avatar = ({ name, size = 36, mono }: { name: string; size?: number; mono?: boolean }) => {
   const initials = name
      .split(' ')
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
   const h = HUES[name.charCodeAt(0) % HUES.length]
   return (
      <span className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold" style={{ width: size, height: size, fontSize: size * 0.36, background: mono ? 'var(--fr-surface-2)' : `hsl(${h} 70% 92%)`, color: mono ? 'var(--fr-text)' : `hsl(${h} 50% 36%)` }}>
         {initials}
      </span>
   )
}

/* ---------- Search + pick ---------- */

export const SearchPick = ({ items, onPick, placeholder, emptyLabel = 'No match', testId, renderItem, size = 'md' }: { items: { id: string; label: string; sub?: string; kind?: string }[]; onPick: (id: string) => void; placeholder: string; emptyLabel?: string; testId?: string; renderItem?: (item: { id: string; label: string; sub?: string; kind?: string }) => ReactNode; size?: 'md' | 'lg' }) => {
   const [q, setQ] = useState('')
   const [open, setOpen] = useState(false)
   const [hi, setHi] = useState(0)
   const ref = useRef<HTMLDivElement>(null)
   const results = useMemo(() => {
      const s = q.trim().toLowerCase()
      const list = s ? items.filter((i) => (i.label + ' ' + (i.sub || '')).toLowerCase().includes(s)) : items
      return list.slice(0, 8)
   }, [q, items])
   useEffect(() => {
      const onDoc = (e: MouseEvent) => {
         if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
      }
      document.addEventListener('mousedown', onDoc)
      return () => document.removeEventListener('mousedown', onDoc)
   }, [])
   const pick = (id: string) => {
      onPick(id)
      setQ('')
      setHi(0)
      setOpen(false)
   }
   return (
      <div ref={ref} className="relative">
         <input
            value={q}
            placeholder={placeholder}
            data-testid={testId}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
               setQ(e.currentTarget.value)
               setOpen(true)
               setHi(0)
            }}
            onKeyDown={(e) => {
               if (e.key === 'ArrowDown') setHi((h) => Math.min(results.length - 1, h + 1))
               if (e.key === 'ArrowUp') setHi((h) => Math.max(0, h - 1))
               if (e.key === 'Enter' && results[hi]) pick(results[hi].id)
               if (e.key === 'Escape') setOpen(false)
            }}
            className={cn('w-full rounded-2xl border border-[var(--fr-border)] bg-white pl-11 pr-4 hover:border-[var(--fr-border-strong)] focus:border-[var(--fr-blue)] focus:outline-none focus:ring-4 focus:ring-[var(--fr-blue-tint)]', size === 'md' ? 'h-12 text-[15px]' : 'h-14 text-[17px]')}
         />
         <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--fr-text-3)]" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
         </svg>
         {open && (
            <div className="absolute z-30 left-0 right-0 mt-1.5 rounded-2xl border border-[var(--fr-border)] bg-white shadow-[var(--fr-shadow)] p-1.5 max-h-64 overflow-auto fr-scroll">
               {results.length === 0 ? (
                  <p className="text-[14px] text-[var(--fr-text-3)] px-3 py-2">{emptyLabel}</p>
               ) : (
                  results.map((r, i) => (
                     <button key={r.id} type="button" onMouseEnter={() => setHi(i)} onClick={() => pick(r.id)} className={cn('w-full text-left rounded-xl px-3 py-2 text-[14px] flex items-center gap-3', i === hi ? 'bg-[var(--fr-blue-tint)]' : '')}>
                        {renderItem ? (
                           renderItem(r)
                        ) : (
                           <>
                              <span className="flex-1 min-w-0 truncate">{r.label}</span>
                              {r.sub && <span className="text-[12px] text-[var(--fr-text-3)] truncate">{r.sub}</span>}
                              {r.kind && <span className={cn('text-[10px] uppercase tracking-wide px-1.5 h-5 inline-flex items-center rounded-full', r.kind === 'stage' ? 'bg-[var(--fr-stage-tint)] text-[var(--fr-stage)]' : 'bg-[var(--fr-tag-tint)] text-[var(--fr-tag)]')}>{r.kind}</span>}
                           </>
                        )}
                     </button>
                  ))
               )}
            </div>
         )}
      </div>
   )
}

/* ---------- The dashboard's voice orb: a soft gradient sphere with a glow ---------- */

export const VoiceOrb = ({ palette, size = 72, live, className }: { palette: [string, string, string]; size?: number; live?: boolean; className?: string }) => (
   <span
      className={cn('fr-orb inline-block shrink-0 rounded-full', live && 'fr-orb-live', className)}
      style={{
         width: size,
         height: size,
         background: `radial-gradient(circle at 32% 28%, ${palette[0]} 0%, ${palette[1]} 52%, ${palette[2]} 100%)`,
         boxShadow: `inset 0 -${Math.round(size * 0.12)}px ${Math.round(size * 0.3)}px rgba(0,0,0,0.10), inset 0 ${Math.round(size * 0.06)}px ${Math.round(size * 0.2)}px rgba(255,255,255,0.35)`,
      }}
      aria-hidden
   />
)

/* ---------- Text ---------- */

export const Title = ({ children, className }: { children: ReactNode; className?: string }) => <h1 className={cn('text-[34px] md:text-[40px] leading-[1.1] font-semibold tracking-[-0.02em] text-[var(--fr-text)] [text-wrap:balance]', className)}>{children}</h1>
export const Lede = ({ children, className }: { children: ReactNode; className?: string }) => <p className={cn('text-[16px] leading-6 text-[var(--fr-text-2)] [text-wrap:pretty]', className)}>{children}</p>

export const GoogleG = () => (
   <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.5 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
      <path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.8-6C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.7l7.8-6z" />
      <path fill="#34A853" d="M24 48c6.2 0 11.6-2 15.4-5.6l-7.5-5.8c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.7-4-13.6-9.6l-7.8 6C6.5 42.6 14.6 48 24 48z" />
   </svg>
)
export const MicrosoftM = () => (
   <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="2" width="9" height="9" fill="#F25022" />
      <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
      <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
      <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
   </svg>
)

export const Icon = ({ name, size = 18, className }: { name: string; size?: number; className?: string }) => {
   const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, className }
   switch (name) {
      case 'check':
         return <svg {...p}><path d="M5 13l4 4L19 7" /></svg>
      case 'chevrons':
         return <svg {...p}><path d="M6 5l7 7-7 7M13 5l7 7-7 7" /></svg>
      case 'arrow-left':
         return <svg {...p}><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
      case 'arrow-right':
         return <svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>
      case 'phone':
         return <svg {...p}><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" /></svg>
      case 'mail':
         return <svg {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
      case 'calendar':
         return <svg {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
      case 'message':
         return <svg {...p}><path d="M21 12a8 8 0 0 1-8 8H8l-5 3 1.2-4.5A8 8 0 1 1 21 12z" /></svg>
      case 'pin':
         return <svg {...p}><path d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></svg>
      case 'shield':
         return <svg {...p}><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" /></svg>
      case 'clock':
         return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      case 'users':
         return <svg {...p}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-4-6" /></svg>
      case 'user':
         return <svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
      case 'voice':
         return <svg {...p}><path d="M4 10v4M8 7v10M12 4v16M16 8v8M20 10v4" /></svg>
      case 'zap':
         return <svg {...p}><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" /></svg>
      case 'card':
         return <svg {...p}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20M6 15h4" /></svg>
      case 'gauge':
         return <svg {...p}><path d="M4 16a8 8 0 1 1 16 0" /><path d="M12 16l4-5" /><circle cx="12" cy="16" r="1.5" fill="currentColor" /></svg>
      case 'file':
         return <svg {...p}><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></svg>
      case 'key':
         return <svg {...p}><circle cx="8" cy="12" r="4" /><path d="M12 12h9M18 12v3M15 12v2" /></svg>
      case 'play':
         return <svg {...p}><path d="M7 5v14l11-7z" fill="currentColor" stroke="none" /></svg>
      case 'stop':
         return <svg {...p}><rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none" /></svg>
      case 'pause':
         return <svg {...p}><path d="M8 5v14M16 5v14" strokeWidth="2.6" /></svg>
      case 'lock':
         return <svg {...p}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
      case 'spark':
         return <svg {...p}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6" /></svg>
      case 'close':
         return <svg {...p}><path d="M6 6l12 12M18 6L6 18" /></svg>
      case 'plus':
         return <svg {...p}><path d="M12 5v14M5 12h14" /></svg>
      case 'home':
         return <svg {...p}><path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9z" /></svg>
      case 'chart':
         return <svg {...p}><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>
      case 'settings':
         return <svg {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>
      case 'trend':
         return <svg {...p}><path d="M3 17l6-6 4 4 8-8M15 7h6v6" /></svg>
      case 'edit':
         return <svg {...p}><path d="M4 20h4l10-10-4-4L4 16v4zM13 7l4 4" /></svg>
      default:
         return null
   }
}
